import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import Navbar from '../components/Navbar';
import { Plus, Edit2, Building2, Users, CheckCircle, XCircle, AlertTriangle, X, Mail, Send, RefreshCw, UserPlus, Trash2, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import BulkEmailModal from '../components/BulkEmailModal';

const AdminPartnerOrgs = () => {
  const { user } = useContext(AuthContext);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInvitesModal, setShowInvitesModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [invites, setInvites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmailPartners, setShowEmailPartners] = useState(false);
  
  const [formData, setFormData] = useState({
    org_name: '',
    primary_contact_name: '',
    primary_email: '',
    primary_phone: '',
    towns_served: [],
    status: 'active',
    donation_tier: '',
    donation_url_override: ''
  });
  
  const [assignData, setAssignData] = useState({
    user_email: '',
    role: 'partner_staff'
  });
  
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'partner_staff'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [orgsResponse, usersResponse, invitesResponse] = await Promise.all([
        axios.get(`${API}/admin/partner-organizations`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/partner-invites`)
      ]);
      setOrganizations(orgsResponse.data || []);
      setUsers(usersResponse.data || []);
      setInvites(invitesResponse.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTownsChange = (e) => {
    const towns = e.target.value.split(',').map(t => t.trim()).filter(t => t);
    setFormData(prev => ({ ...prev, towns_served: towns }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingOrg) {
        await axios.put(`${API}/admin/partner-organizations/${editingOrg.id}`, formData);
        toast.success('Organization updated');
      } else {
        await axios.post(`${API}/admin/partner-organizations`, formData);
        toast.success('Organization created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save organization');
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    
    try {
      await axios.post(`${API}/admin/users/${assignData.user_email}/assign-partner?partner_org_id=${selectedOrg.id}&role=${assignData.role}`);
      toast.success(`User assigned to ${selectedOrg.org_name}`);
      setShowAssignModal(false);
      setAssignData({ user_email: '', role: 'partner_staff' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign user');
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      org_name: org.org_name,
      primary_contact_name: org.primary_contact_name,
      primary_email: org.primary_email,
      primary_phone: org.primary_phone,
      towns_served: org.towns_served || [],
      status: org.status || 'active',
      donation_tier: org.donation_tier || '',
      donation_url_override: org.donation_url_override || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      org_name: '',
      primary_contact_name: '',
      primary_email: '',
      primary_phone: '',
      towns_served: [],
      status: 'active',
      donation_tier: '',
      donation_url_override: ''
    });
    setEditingOrg(null);
  };

  const openAssignModal = (org) => {
    setSelectedOrg(org);
    setShowAssignModal(true);
  };
  
  const openInviteModal = (org) => {
    setSelectedOrg(org);
    setInviteData({ email: '', role: 'partner_staff' });
    setShowInviteModal(true);
  };
  
  const openInvitesModal = (org) => {
    setSelectedOrg(org);
    setShowInvitesModal(true);
  };
  
  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!selectedOrg) return;
    
    try {
      await axios.post(`${API}/partner-invites`, {
        email: inviteData.email,
        org_id: selectedOrg.id,
        role: inviteData.role
      });
      toast.success(`Invite sent to ${inviteData.email}`);
      setShowInviteModal(false);
      setInviteData({ email: '', role: 'partner_staff' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };
  
  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/partner-invites/${inviteId}/resend`);
      toast.success('Invite resent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };
  
  const handleRevokeInvite = async (inviteId) => {
    if (!confirm('Are you sure you want to revoke this invite?')) return;
    
    try {
      await axios.post(`${API}/partner-invites/${inviteId}/revoke`);
      toast.success('Invite revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke invite');
    }
  };
  
  const handleUnassignUser = async (email, orgName) => {
    if (!window.confirm(`Remove ${email} from ${orgName}? They will become a regular user.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}/unassign-partner`);
      toast.success(`User removed from ${orgName}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  // Get pending invites for an org
  const getOrgInvites = (orgId) => {
    return invites.filter(inv => inv.org_id === orgId && inv.status === 'pending');
  };

  // Get users assigned to each organization
  const getOrgUsers = (orgId) => {
    return users.filter(u => u.partner_org_id === orgId);
  };

  // Get unassigned users (for assigning)
  const unassignedUsers = users.filter(u => !u.partner_org_id && u.role !== 'admin');

  return (
    <div className="min-h-screen" data-testid="admin-partner-orgs">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-3">Partner Organizations</h1>
            <p className="text-base text-[#666666]">Manage partner organizations and assign users</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowEmailPartners(true)}
              className="btn-outline flex items-center gap-2"
              data-testid="email-partners-button"
              title="Send a bulk email to partner organizations"
            >
              <Mail className="w-5 h-5" />
              Email Partners
            </button>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="btn-primary flex items-center gap-2"
              data-testid="create-org-button"
            >
              <Plus className="w-5 h-5" />
              Add Organization
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : organizations.length === 0 ? (
          <div className="card text-center py-12">
            <Building2 className="w-12 h-12 text-[#E9DDCF] mx-auto mb-4" />
            <p className="text-[#666666] mb-4">No partner organizations yet</p>
            <button onClick={() => setShowModal(true)} className="btn-primary">Create First Organization</button>
          </div>
        ) : (() => {
          const q = searchQuery.trim().toLowerCase();
          const filteredOrganizations = q
            ? organizations.filter(o =>
                (o.org_name || '').toLowerCase().includes(q) ||
                (o.primary_contact_name || '').toLowerCase().includes(q) ||
                (o.primary_email || '').toLowerCase().includes(q) ||
                (o.primary_phone || '').toLowerCase().includes(q)
              )
            : organizations;

          return (
            <>
              {/* Search bar */}
              <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
                <input
                  type="text"
                  className="input-field pl-10 pr-10"
                  placeholder="Search by name, contact, email, or phone…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="org-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-[#888] hover:bg-[#FBF4EA] hover:text-[#222]"
                    title="Clear search"
                    data-testid="org-search-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {searchQuery && (
                  <p className="text-xs text-[#666] mt-1" data-testid="org-search-count">
                    {filteredOrganizations.length} of {organizations.length} organization{organizations.length === 1 ? '' : 's'} match
                  </p>
                )}
              </div>

              {filteredOrganizations.length === 0 ? (
                <div className="card text-center py-10" data-testid="org-search-empty">
                  <Search className="w-10 h-10 text-[#E9DDCF] mx-auto mb-3" />
                  <p className="text-[#666]">No organizations found.</p>
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-[#8B2E3C] hover:underline text-sm mt-2"
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                <div className="grid gap-6">
                  {filteredOrganizations.map(org => {
                    const orgUsers = getOrgUsers(org.id);
              
              return (
                <div key={org.id} className="card" data-testid={`org-card-${org.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Organization Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-12 h-12 bg-[#8B2E3C] rounded-lg flex items-center justify-center shrink-0">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-[#222222]">{org.org_name}</h2>
                          <div className="flex items-center gap-2 mt-1">
                            {org.status === 'active' ? (
                              <span className="badge badge-approved">Active</span>
                            ) : (
                              <span className="badge bg-red-100 text-red-700">Suspended</span>
                            )}
                            {org.donation_tier && (
                              <span className="badge bg-[#C9A227] text-white">{org.donation_tier}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid sm:grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <span className="font-medium">Primary Contact:</span>
                          <div className="text-[#666666]">{org.primary_contact_name}</div>
                        </div>
                        <div>
                          <span className="font-medium">Email:</span>
                          <div className="text-[#666666]">{org.primary_email}</div>
                        </div>
                        <div>
                          <span className="font-medium">Phone:</span>
                          <div className="text-[#666666]">{org.primary_phone}</div>
                        </div>
                        <div>
                          <span className="font-medium">Towns Served:</span>
                          <div className="text-[#666666]">{(org.towns_served || []).join(', ') || 'All'}</div>
                        </div>
                      </div>
                      
                      <div className="text-xs text-[#666666]">
                        Created: {format(new Date(org.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                    
                    {/* Assigned Users */}
                    <div className="lg:w-80 shrink-0">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-[#222222]">Assigned Users ({orgUsers.length})</h3>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openInviteModal(org)}
                            className="text-[#8B2E3C] text-sm hover:underline flex items-center gap-1"
                            data-testid={`invite-user-btn-${org.id}`}
                          >
                            <Mail className="w-3 h-3" />
                            Invite
                          </button>
                          <button
                            onClick={() => openAssignModal(org)}
                            className="text-[#8B2E3C] text-sm hover:underline"
                            data-testid={`assign-user-btn-${org.id}`}
                          >
                            + Assign
                          </button>
                        </div>
                      </div>
                      
                      {/* Pending Invites */}
                      {getOrgInvites(org.id).length > 0 && (
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-[#C9A227]">Pending Invites ({getOrgInvites(org.id).length})</span>
                            <button
                              onClick={() => openInvitesModal(org)}
                              className="text-xs text-[#666666] hover:text-[#8B2E3C]"
                            >
                              View all
                            </button>
                          </div>
                          {getOrgInvites(org.id).slice(0, 2).map(inv => (
                            <div key={inv.id} className="flex items-center justify-between bg-yellow-50 rounded-lg p-2 text-xs mb-1">
                              <div className="flex items-center gap-2">
                                <Clock className="w-3 h-3 text-[#C9A227]" />
                                <span>{inv.email}</span>
                              </div>
                              <span className="text-[#666666]">{inv.role === 'partner_admin' ? 'Admin' : 'Staff'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {orgUsers.length === 0 ? (
                        <p className="text-sm text-[#666666]">No users assigned</p>
                      ) : (
                        <div className="space-y-2">
                          {orgUsers.map(u => (
                            <div key={u.email} className="flex items-center justify-between bg-[#FBF4EA] rounded-lg p-2 text-sm">
                              <div>
                                <div className="font-medium">{u.name}</div>
                                <div className="text-xs text-[#666666]">{u.email}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs bg-white px-2 py-1 rounded">
                                  {u.role === 'partner_admin' ? 'Admin' : 'Staff'}
                                </span>
                                <button
                                  onClick={() => handleUnassignUser(u.email, org.org_name)}
                                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Remove from organization"
                                  data-testid={`remove-user-${u.email}`}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2">
                      <button
                        onClick={() => handleEdit(org)}
                        className="p-2 text-[#8B2E3C] hover:bg-[#FBF4EA] rounded-lg transition-colors"
                        data-testid={`edit-org-btn-${org.id}`}
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Create/Edit Organization Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="org-modal">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#222222]">
                  {editingOrg ? 'Edit Organization' : 'Add Organization'}
                </h2>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Organization Name *</label>
                    <input
                      type="text"
                      name="org_name"
                      className="input-field"
                      value={formData.org_name}
                      onChange={handleInputChange}
                      required
                      data-testid="org-name-input"
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Primary Contact Name *</label>
                      <input
                        type="text"
                        name="primary_contact_name"
                        className="input-field"
                        value={formData.primary_contact_name}
                        onChange={handleInputChange}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Contact Email *</label>
                      <input
                        type="email"
                        name="primary_email"
                        className="input-field"
                        value={formData.primary_email}
                        onChange={handleInputChange}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="primary_phone"
                        className="input-field"
                        value={formData.primary_phone}
                        onChange={handleInputChange}
                        required
                        placeholder="555-123-4567"
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Status</label>
                      <select
                        name="status"
                        className="input-field"
                        value={formData.status}
                        onChange={handleInputChange}
                        data-testid="status-select"
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Towns Served (comma-separated)</label>
                    <input
                      type="text"
                      className="input-field"
                      value={(formData.towns_served || []).join(', ')}
                      onChange={handleTownsChange}
                      placeholder="Ossipee, Effingham, Freedom"
                      data-testid="towns-input"
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Donation Tier</label>
                      <select
                        name="donation_tier"
                        className="input-field"
                        value={formData.donation_tier}
                        onChange={handleInputChange}
                        data-testid="donation-tier-select"
                      >
                        <option value="">None</option>
                        <option value="Bronze">Bronze</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold">Gold</option>
                        <option value="Platinum">Platinum</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Custom Donation URL</label>
                      <input
                        type="url"
                        name="donation_url_override"
                        className="input-field"
                        value={formData.donation_url_override}
                        onChange={handleInputChange}
                        placeholder="https://..."
                        data-testid="donation-url-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    data-testid="save-org-button"
                  >
                    {editingOrg ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Assign User Modal */}
      {showAssignModal && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="assign-modal">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#222222]">Assign User to {selectedOrg.org_name}</h2>
                <button onClick={() => setShowAssignModal(false)} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleAssignUser}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Select User *</label>
                    <select
                      className="input-field"
                      value={assignData.user_email}
                      onChange={(e) => setAssignData(prev => ({ ...prev, user_email: e.target.value }))}
                      required
                      data-testid="assign-user-select"
                    >
                      <option value="">Choose a user...</option>
                      {unassignedUsers.map(u => (
                        <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                    {unassignedUsers.length === 0 && (
                      <p className="text-sm text-[#666666] mt-2">No unassigned users available. Use "Invite" to invite new users.</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Role *</label>
                    <select
                      className="input-field"
                      value={assignData.role}
                      onChange={(e) => setAssignData(prev => ({ ...prev, role: e.target.value }))}
                      required
                      data-testid="assign-role-select"
                    >
                      <option value="partner_staff">Partner Staff</option>
                      <option value="partner_admin">Partner Admin</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!assignData.user_email}
                    data-testid="confirm-assign-button"
                  >
                    Assign User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="invite-modal">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#222222]">Invite User to {selectedOrg.org_name}</h2>
                <button onClick={() => setShowInviteModal(false)} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <form onSubmit={handleSendInvite}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      className="input-field"
                      value={inviteData.email}
                      onChange={(e) => setInviteData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="partner@example.com"
                      required
                      data-testid="invite-email-input"
                    />
                    <p className="text-xs text-[#666666] mt-1">An invitation link will be sent to this email.</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Role *</label>
                    <select
                      className="input-field"
                      value={inviteData.role}
                      onChange={(e) => setInviteData(prev => ({ ...prev, role: e.target.value }))}
                      required
                      data-testid="invite-role-select"
                    >
                      <option value="partner_staff">Partner Staff (View only)</option>
                      <option value="partner_admin">Partner Admin (Full access)</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex items-center gap-2"
                    data-testid="send-invite-button"
                  >
                    <Send className="w-4 h-4" />
                    Send Invite
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Invites Modal */}
      {showInvitesModal && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="invites-modal">
          <div className="bg-white rounded-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#222222]">Pending Invites for {selectedOrg.org_name}</h2>
                <button onClick={() => setShowInvitesModal(false)} className="text-[#666666] hover:text-[#222222]">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {getOrgInvites(selectedOrg.id).length === 0 ? (
                <p className="text-[#666666] text-center py-8">No pending invites</p>
              ) : (
                <div className="space-y-3">
                  {getOrgInvites(selectedOrg.id).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between bg-[#FBF4EA] rounded-lg p-4">
                      <div>
                        <div className="font-medium">{inv.email}</div>
                        <div className="text-xs text-[#666666]">
                          {inv.role === 'partner_admin' ? 'Partner Admin' : 'Partner Staff'} • 
                          Sent {inv.send_count}x • 
                          Expires {format(new Date(inv.expires_at), 'MMM d, yyyy')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleResendInvite(inv.id)}
                          className="p-2 text-[#8B2E3C] hover:bg-white rounded-lg transition-colors"
                          title="Resend invite"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRevokeInvite(inv.id)}
                          className="p-2 text-red-600 hover:bg-white rounded-lg transition-colors"
                          title="Revoke invite"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => { setShowInvitesModal(false); openInviteModal(selectedOrg); }}
                  className="btn-primary flex items-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite New User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BulkEmailModal
        isOpen={showEmailPartners}
        onClose={() => setShowEmailPartners(false)}
        scope="admin"
        prefill={{ recipient_kind: 'partners', filter_type: 'all' }}
      />
    </div>
  );
};

export default AdminPartnerOrgs;
